import { useState } from 'react';
import { buildDemoRound, getRandomDemoMovie } from './demoQuestions';
import styles from './DemoQuiz.module.css';

function DemoMovieSelector({ movie, onShuffle }) {
    return (
        <div className={styles.movieSelector}>
            <span>Now Verifying</span>
            <strong>{movie.title}</strong>
            <button type="button" onClick={onShuffle}>Try another title</button>
        </div>
    );
}

function DemoProgress({ current, total, complete = false }) {
    const pct = complete ? 100 : (current / total) * 100;
    return (
        <div className={styles.progressWrap} aria-label={`Verification progress ${Math.round(pct)} percent`}>
            <div className={styles.progressMeta}>
                <span>{complete ? 'Verification Complete' : `Question ${current + 1} of ${total}`}</span>
                <strong>{Math.round(pct)}%</strong>
            </div>
            <div className={styles.progressTrack}><span style={{ width: `${pct}%` }} /></div>
        </div>
    );
}

function DemoQuestion({ question, selected, onSelect }) {
    return (
        <div className={styles.questionPane}>
            <p className={styles.difficulty}>{question.difficulty}</p>
            <h3>{question.question}</h3>
            <div className={styles.answerGrid}>
                {question.choices.map((choice) => {
                    const isSelected = selected === choice;
                    const isCorrect = choice === question.answer;
                    const resultClass = selected && isSelected ? (isCorrect ? styles.correct : styles.incorrect) : '';
                    return (
                        <button
                            type="button"
                            className={`${styles.answerButton} ${resultClass}`}
                            disabled={Boolean(selected)}
                            onClick={() => onSelect(choice)}
                            key={choice}
                        >
                            <span>{isSelected ? (isCorrect ? '✓' : '✕') : '○'}</span>
                            {choice}
                        </button>
                    );
                })}
            </div>
            {selected && (
                <div className={selected === question.answer ? styles.feedbackCorrect : styles.feedbackIncorrect} role="status">
                    <strong>{selected === question.answer ? '✓ Correct' : '✗ Incorrect'}</strong>
                    <span>{question.explanation}</span>
                </div>
            )}
        </div>
    );
}

function getDemoTier(score, total) {
    const pct = total > 0 ? score / total : 0;

    if (pct === 1) return { name: 'Gold', detail: 'Perfect verification. Legendary is now in reach.' };
    if (pct >= 0.7) return { name: 'Silver', detail: 'Strong proof. One cleaner run can push this to Gold.' };
    return { name: 'Bronze', detail: 'You proved the basics. Keep going to climb the tiers.' };
}

function DemoResults({ movie, score, total }) {
    const tier = getDemoTier(score, total);

    return (
        <div className={styles.resultsPane}>
            <DemoProgress current={1} total={1} complete />
            <div className={styles.coinOrbit} aria-hidden="true">
                {['Bronze', 'Silver', 'Gold', 'Legendary'].map((tier) => <span key={tier}>{tier}</span>)}
            </div>
            <div className={styles.blurredCoin}>{movie.posterEmoji}</div>
            <p className={styles.scoreLine}>{score}/{total} correct</p>
            <h3>You earned {tier.name}.</h3>
            <p>{tier.detail} Create a free account to save your result and start collecting coins.</p>
            <div className={styles.resultCtas}>
                <a href="/register">Start Verifying</a>
                <a href="/collections">See Collections</a>
            </div>
        </div>
    );
}

function DemoQuiz() {
    const [movie, setMovie] = useState(() => getRandomDemoMovie());
    const [questions, setQuestions] = useState(() => buildDemoRound(movie));
    const [index, setIndex] = useState(0);
    const [selected, setSelected] = useState('');
    const [score, setScore] = useState(0);
    const [complete, setComplete] = useState(false);

    const restartWithMovie = () => {
        setIndex(0);
        setSelected('');
        setScore(0);
        setComplete(false);
        const nextMovie = getRandomDemoMovie();
        setMovie(nextMovie);
        setQuestions(buildDemoRound(nextMovie));
    };

    const handleSelect = (choice) => {
        if (selected) return;
        setSelected(choice);
        if (choice === questions[index].answer) {
            setScore((value) => value + 1);
        }
        window.setTimeout(() => {
            setSelected('');
            if (index + 1 >= questions.length) {
                setComplete(true);
            } else {
                setIndex((value) => value + 1);
            }
        }, 2000);
    };

    return (
        <section className={styles.demoCard} style={{ '--demo-accent': movie.accent }} aria-label="Interactive Aluvo verification demo">
            <DemoMovieSelector movie={movie} onShuffle={restartWithMovie} />
            <div className={styles.demoShell}>
                <div className={styles.posterCard}>
                    <span>{movie.posterEmoji}</span>
                    <strong>{movie.title}</strong>
                    <em>{movie.year}</em>
                </div>
                <div className={styles.quizPanel}>
                    {complete ? (
                        <DemoResults movie={movie} score={score} total={questions.length} />
                    ) : (
                        <>
                            <DemoProgress current={index} total={questions.length} />
                            <DemoQuestion question={questions[index]} selected={selected} onSelect={handleSelect} />
                        </>
                    )}
                </div>
            </div>
        </section>
    );
}

export default DemoQuiz;
